<template>
  <div class="video-landmarks-player">
    <v-card flat class="video-container pa-0">
      <div ref="playerShell" class="player-shell">
        <div v-if="!hasError" class="video-above-video-slot">
          <slot name="above-video"></slot>
        </div>

        <div
          ref="videoWrapper"
          class="video-wrapper"
          @mousemove="onMouseMove"
          @mouseleave="onMouseLeave"
          @click="onVideoClick"
        >
          <video
            ref="videoElement"
            :src="videoUrl"
            class="interview-video"
            :class="{ 'interview-video--fill': fillContainerWidth }"
            :style="videoElementStyle"
            preload="metadata"
            playsinline
            @loadedmetadata="onVideoLoaded"
            @timeupdate="onTimeUpdate"
            @play="onVideoPlay"
            @pause="onVideoPause"
            @loadstart="onLoadStart"
            @canplay="onCanPlay"
            @waiting="onWaiting"
            @error="onVideoError"
          ></video>

          <canvas
            ref="landmarksCanvas"
            class="landmarks-canvas"
            :style="{ visibility: landmarksAvailable ? 'visible' : 'hidden' }"
          ></canvas>

          <!-- Use $slots.overlay here (not a computed): Vue 2 does not reliably invalidate
               computed caches when parent v-if toggles named slots, so overlays can stay hidden after resize. -->
          <div v-if="$slots.overlay" class="video-overlay-slot" @click.stop>
            <slot name="overlay"></slot>
          </div>

          <transition name="playback-feedback-fade">
            <div v-if="showPlaybackFeedback" class="playback-feedback-overlay" aria-hidden="true">
              <div class="playback-feedback-badge">
                <v-icon size="40" color="white">{{ playbackFeedbackIcon }}</v-icon>
              </div>
            </div>
          </transition>

          <div v-show="isLoading" class="loading-overlay">
            <v-progress-circular indeterminate color="white" size="64" />
            <p class="loading-text">Loading video...</p>
          </div>

          <div v-if="hasError" class="error-overlay">
            <v-icon large color="white">mdi-alert-circle</v-icon>
            <p class="error-text">Error loading video</p>
            <v-btn color="white" outlined @click="retryVideo">Retry</v-btn>
          </div>

          <div v-if="isAnalyzing" class="analysis-overlay">
            <div class="analysis-content">
              <v-progress-circular indeterminate :size="64" :width="5" color="white" />
              <p class="analysis-step-text mt-4">Analysis in progress</p>
              <p class="analysis-hint-text mt-2">
                The video will be available to watch with the analysis results once it finishes.
              </p>
            </div>
          </div>
        </div>

        <div v-if="!hasError" class="video-below-video-slot">
          <slot name="below-video"></slot>
        </div>

        <div v-if="!hasError" class="video-player-footer">
          <div class="timeline-section" @click="onTimelineClick">
            <div
              ref="timelineVisualStack"
              class="timeline-visual-stack"
              @mousemove.passive="onTimelineVisualHover"
              @mouseleave="onTimelineVisualLeave"
            >
              <div class="custom-timeline">
                <div
                  ref="timelineTrack"
                  class="timeline-track"
                  :class="{ 'timeline-track--picking': reportMissingActive }"
                >
                  <div
                    class="timeline-progress"
                    :style="{ width: timelineLeftPercent(currentTime) }"
                  ></div>
                  <div
                    v-if="showHoverPreview"
                    class="timeline-progress timeline-progress-preview"
                    :style="timelinePreviewStyle()"
                  ></div>

                  <div
                    v-for="(block, index) in semanticBlockMarkers"
                    :key="block.key || ('block-' + index)"
                    :ref="'coherenceBlockMarker'"
                    class="coherence-block-marker"
                    :style="timelineBlockStyle(block)"
                    :title="block.description"
                    @click.stop="jumpToTime(block.startSeconds)"
                    @mousedown.stop
                  ></div>

                  <!-- Gesture alert markers over the timeline removed by product decision: not displayed anymore. -->


                  <div
                    v-if="incoherenceOverlayStyle"
                    class="incoherence-range-marker"
                    :style="incoherenceOverlayStyle"
                    title="Lie"
                  ></div>

                  <div
                    class="timeline-thumb"
                    :class="{ dragging: isDragging }"
                    :style="{ left: timelineThumbLeftPercent(currentTime) }"
                    @mousedown.stop="startDrag"
                  ></div>
                </div>
              </div>
              <div
                v-if="false"
                class="timeline-guide-line timeline-guide-line-preview"
                :style="{ left: duration > 0 ? `${(timelineHoverTime / duration) * 100}%` : '0%' }"
              ></div>
              <div
                v-if="isTimelineHovering && Number.isFinite(timelineHoverTime) && duration > 0"
                class="timeline-hover-time-bubble"
                :class="{ 'timeline-hover-time-bubble--picking': reportMissingActive }"
                :style="{ left: `${(timelineHoverTime / duration) * 100}%` }"
              >
                {{ formatTimeWithMs(timelineHoverTime) }}
              </div>
            </div>
          </div>

          <div class="footer-controls-row">
            <div class="footer-controls-left d-flex align-center flex-wrap">
              <div class="play-pause-control">
                <v-btn
                  icon
                  small
                  text
                  class="footer-control-btn play-pause-btn"
                  :disabled="isLoading"
                  @click.stop="togglePlayPause"
                >
                  <v-icon size="24" color="white">
                    {{ isPlaying ? 'mdi-pause' : 'mdi-play' }}
                  </v-icon>
                </v-btn>
              </div>

              <div
                class="volume-hover-wrap d-flex align-center ml-1"
                @mouseenter="volumeHover = true"
                @mouseleave="volumeHover = false"
              >
                <v-btn icon small text class="footer-control-btn" @click.stop="toggleMute">
                  <v-icon small color="white">
                    {{ isMuted || volume === 0 ? 'mdi-volume-off' : 'mdi-volume-high' }}
                  </v-icon>
                </v-btn>
                <div v-show="volumeHover" class="volume-slider-inline px-2">
                  <v-slider
                    v-model="volume"
                    dense
                    hide-details
                    height="28"
                    color="white"
                    track-color="rgba(255, 255, 255, 0.35)"
                    thumb-color="white"
                    :max="100"
                    :min="0"
                    @input="onVolumeInput"
                  />
                </div>
              </div>
            </div>

            <div class="footer-controls-center d-flex align-center justify-center">
              <span class="time-display-footer">
                {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
              </span>
            </div>

            <div class="footer-controls-right d-flex align-center">
              <v-menu
                v-if="showFooterSettingsMenu"
                offset-y
                top
                :close-on-content-click="false"
                content-class="footer-settings-menu-panel"
              >
                <template #activator="{ on, attrs }">
                  <v-btn icon small text class="mr-1 footer-control-btn" v-bind="attrs" v-on="on">
                    <v-icon small color="white">mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense min-width="280" class="footer-settings-menu-list">
                  <slot name="settings-menu"></slot>
                </v-list>
              </v-menu>
              <template v-if="!showFooterSettingsMenu">
                <v-tooltip v-if="showOverlayToggle" top>
                  <template #activator="{ on }">
                    <v-btn
                      icon
                      small
                      class="mr-1 footer-control-btn"
                      v-on="on"
                      @click.stop="onToggleOverlays"
                    >
                      <v-icon small color="white">
                        {{ overlaysVisible ? 'mdi-layers-off' : 'mdi-layers' }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ overlaysVisible ? 'Hide overlays' : 'Show overlays' }}</span>
                </v-tooltip>
                <v-tooltip v-if="showViewModeToggle" top>
                  <template #activator="{ on }">
                    <v-btn
                      icon
                      small
                      class="mr-1 footer-control-btn"
                      v-on="on"
                      @click.stop="onToggleAnalysisViewMode"
                    >
                      <v-icon small color="white">
                        {{ analysisViewMode === 'coherence' ? 'mdi-human-handsup' : 'mdi-shield-search' }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>
                    {{ analysisViewMode === 'coherence' ? 'Non Verbal Analysis' : 'Coherence Analysis' }}
                  </span>
                </v-tooltip>
              </template>
              <v-menu offset-y top>
                <template #activator="{ on, attrs }">
                  <v-btn small text class="speed-btn footer-text-btn white--text" v-bind="attrs" v-on="on">
                    {{ playbackRate }}x
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item
                    v-for="speed in speedOptions"
                    :key="speed.value"
                    @click="changePlaybackSpeed(speed.value)"
                  >
                    <v-list-item-title>{{ speed.text }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-btn icon small text class="ml-1 footer-control-btn" @click.stop="toggleFullscreen">
                <v-icon color="white">{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { LandmarksRenderer } from '@/utils/landmarksRenderer';
import { detectAlerts } from '@/utils/gestureAnalysisUtils';
import {
  blockScoreForCoherenceMode,
  coherenceBandHex,
} from '@/utils/coherenceSemanticsTimeline';

export default {
  name: 'VideoLandmarksPlayer',

  props: {
    // Required: store key for loading landmarks from the analysisData module: the composite
    // analysisKey '<sourceType>:<sourceId>:<runId|active>' (M2) or a plain numeric id.
    analysisId: {
      type: [Number, String],
      required: true,
    },
    videoUrl: {
      type: String,
      required: true,
    },
    gestureData: {
      type: Array,
      default: () => [],
    },
    /** When non-empty, timeline markers are built from coherence alerts (coherence_after) instead of gesture rows. */
    coherenceTimelineAlerts: {
      type: Array,
      default: () => [],
    },
    /** Active NEXT Semantica blocks in current mode; used as grouped ranges in timeline. */
    coherenceTimelineBlocks: {
      type: Array,
      default: () => [],
    },
    /** Full semantic modes payload (includes diarization words + per-alert blocks). */
    coherenceSemanticModes: {
      type: Object,
      default: null,
    },
    /** 'alert_major' | 'computed_subtraction'. */
    coherenceScoreMode: {
      type: String,
      default: 'computed_subtraction',
    },
    /** Keeps timeline/chart in sync when segment strategy changes (same source as NEXT panel). */
    coherenceSegmentationTab: {
      type: String,
      default: 'idea',
    },
    /** From analysis config; must match CoherenceSemanticsPanel bands for chart colors. */
    semaphoreThresholdGood: {
      type: Number,
      default: 80,
    },
    semaphoreThresholdWarn: {
      type: Number,
      default: 50,
    },
    // DEPRECATED: landmarksData prop is no longer used - landmarks are loaded from store
    landmarksData: {
      type: Array,
      default: () => [],
    },
    showLandmarks: {
      type: Boolean,
      default: false,
    },
    isAnalyzing: {
      type: Boolean,
      default: false,
    },
    analysisProgress: {
      type: Number,
      default: 0,
    },
    analysisStep: {
      type: String,
      default: '',
    },
    autoPauseOnRedAlert: {
      type: Boolean,
      default: false,
    },
    hideOrangeAlerts: {
      type: Boolean,
      default: false,
    },
    landmarksToggle: {
      type: Object,
      default: () => ({
        face: true,
        hands: true,
        gaze: true,
      }),
    },
    /** When true, video expands to container width (coherence layout). */
    fillContainerWidth: {
      type: Boolean,
      default: false,
    },
    isManualAnalysis: {
      type: Boolean,
      default: false,
    },
    analysisViewMode: {
      type: String,
      default: 'coherence',
    },
    showViewModeToggle: {
      type: Boolean,
      default: false,
    },
    showOverlayToggle: {
      type: Boolean,
      default: false,
    },
    overlaysVisible: {
      type: Boolean,
      default: true,
    },
    /** When true, footer shows a dots menu; use slot `settings-menu` for list items. */
    showFooterSettingsMenu: {
      type: Boolean,
      default: false,
    },
    /** Coherence mode: only show timeline markers for alerts active at the current playhead. */
    coherenceTimelineActiveOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * When true, clicking the timeline does not seek the video; instead emits
     * `pick-timestamp` with the seconds where the user clicked. Used by the
     * "Report missing gesture" flow.
     */
    reportMissingActive: {
      type: Boolean,
      default: false,
    },
    /**
     * Labeled lie range in milliseconds ({ startMs, endMs }) or null.
     * Rendered as one persistent overlay block on the timeline.
     */
    incoherenceRangeMs: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      currentTime: 0,
      duration: 0,
      isPlaying: false,
      isLoading: true,
      hasError: false,
      isFullscreen: false,

      isDragging: false,
      isTimelineHovering: false,
      timelineHoverTime: null,
      showPlaybackFeedback: false,
      playbackFeedbackIcon: 'mdi-play',
      playbackFeedbackTimeout: null,

      playbackRate: 1,
      volume: 100,
      isMuted: false,

      speedOptions: [
        { value: 0.5, text: '0.5x' },
        { value: 0.75, text: '0.75x' },
        { value: 1, text: 'Normal' },
        { value: 1.25, text: '1.25x' },
        { value: 1.5, text: '1.5x' },
        { value: 2, text: '2x' },
      ],

      gestureMarkers: [],
      semanticBlockMarkers: [],
      coherenceDipGraphSamples: [],
      coherenceTimelineRaf: null,
      lastCoherenceActiveRebuildAtMs: 0,
      timelineWidthRaf: null,
      timelineHoverRaf: null,
      pendingTimelineHoverClientX: null,
      timelineTrackWidthPx: null,
      lastVideoSurfaceToggleAtMs: 0,
      suppressAutoPauseUntilMs: 0,
      landmarksRenderer: null,
      debouncedRenderLandmarks: null,
      resizeObserver: null,
      lastCanvasSize: { width: 0, height: 0, x: 0, y: 0 },

      isPausedByRedAlert: false,
      lastRedAlertTime: null,
      wasPlayingBeforeDrag: false,
      
      // Landmarks loading state (for showing loading indicator during seek)
      isLoadingLandmarksWindow: false,

      volumeHover: false,
    };
  },

  computed: {
    ...mapGetters('analysisData', [
      'getLandmarksAtTime',
      'hasLandmarksWindow',
      'getLandmarksWindow',
      'isLoadingLandmarks',
      'getWindowIndex',
      'getWindowSize',
    ]),

    // Check if landmarks are available around the current playhead
    landmarksAvailable() {
      const currentWindow = this.getWindowIndex(this.currentTime || 0);
      return (
        this.hasLandmarksWindow(this.analysisId, currentWindow) ||
        this.hasLandmarksWindow(this.analysisId, currentWindow - 1) ||
        this.hasLandmarksWindow(this.analysisId, currentWindow + 1)
      );
    },

    toggleOptions() {
      return {
        face: this.landmarksToggle?.face !== false,
        hands: this.landmarksToggle?.hands !== false,
        gaze: this.landmarksToggle?.gaze !== false,
      };
    },

    showCoherenceDipGraph() {
      return (
        this.analysisViewMode === 'coherence' &&
        Array.isArray(this.coherenceDipGraphSamples) &&
        this.coherenceDipGraphSamples.length > 0
      );
    },
    hoverPreviewColor() {
      return 'rgba(255, 255, 255, 0.35)';
    },
    coherenceDipGraphHeight() {
      return 64;
    },
    effectiveTimelineTime() {
      if (this.isTimelineHovering && Number.isFinite(this.timelineHoverTime)) {
        return this.timelineHoverTime;
      }
      return this.currentTime;
    },
    showHoverPreview() {
      return (
        this.isTimelineHovering &&
        Number.isFinite(this.timelineHoverTime) &&
        Math.abs(this.timelineHoverTime - this.currentTime) > 0.05
      );
    },

    /** Same percent math as semantic block markers; null hides the lie overlay. */
    incoherenceOverlayStyle() {
      const startMs = Number(this.incoherenceRangeMs?.startMs);
      const endMs = Number(this.incoherenceRangeMs?.endMs);
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
        return null;
      }
      const startRatio = this.timelineRatio(startMs / 1000);
      const endRatio = this.timelineRatio(endMs / 1000);
      if (endRatio <= startRatio) return null;
      return {
        left: this.timelinePercentFromRatio(startRatio),
        width: this.timelinePercentFromRatio(endRatio - startRatio),
      };
    },

    /** Video size is controlled by CSS (fills .video-wrapper); avoid inline caps */
    videoElementStyle() {
      return {};
    },
  },

  watch: {
    gestureData: {
      immediate: true,
      handler() {
        this.rebuildTimelineMarkers();
      },
    },

    coherenceTimelineAlerts: {
      deep: true,
      immediate: true,
      handler() {
        this.rebuildTimelineMarkers();
      },
    },
    coherenceTimelineBlocks: {
      deep: true,
      immediate: true,
      handler() {
        this.rebuildTimelineMarkers();
      },
    },
    coherenceScoreMode() {
      this.rebuildTimelineMarkers();
    },

    coherenceSegmentationTab() {
      this.rebuildTimelineMarkers();
    },

    semaphoreThresholdGood() {
      this.rebuildTimelineMarkers();
    },

    semaphoreThresholdWarn() {
      this.rebuildTimelineMarkers();
    },

    analysisViewMode() {
      this.rebuildTimelineMarkers();
    },

    currentTime(newTime) {
      this.$emit('timeupdate', newTime);
      this.debouncedRenderLandmarksWithPrefetch(newTime);

      if (this.autoPauseOnRedAlert) {
        this.checkForRedAlert(newTime);
      }

      if (
        this.coherenceTimelineActiveOnly &&
        this.analysisViewMode === 'coherence' &&
        !this.hasSemanticBlocksTimeline() &&
        Array.isArray(this.coherenceTimelineAlerts) &&
        this.coherenceTimelineAlerts.length > 0
      ) {
        const nowMs = performance.now();
        if (nowMs - Number(this.lastCoherenceActiveRebuildAtMs || 0) >= 120 && !this.coherenceTimelineRaf) {
          this.coherenceTimelineRaf = requestAnimationFrame(() => {
            this.coherenceTimelineRaf = null;
            this.lastCoherenceActiveRebuildAtMs = performance.now();
            this.rebuildTimelineMarkers();
          });
        }
      }

      // Check if landmarks are now available for this time (may have been loaded in background)
      this.$nextTick(() => {
        const windowIndex = this.getWindowIndex(newTime);
        if (this.hasLandmarksWindow(this.analysisId, windowIndex)) {
          // Window is now loaded, try to render again
          const landmarksFrame = this.getLandmarksAtTime(this.analysisId, newTime);
          if (landmarksFrame) {
            this.renderLandmarksFrame(newTime, landmarksFrame);
          }
        }
      });
    },

    landmarksToggle() {
      this.$nextTick(() => {
        this.renderLandmarksAtTime(this.currentTime);
      });
    },

    // Watch for landmarks becoming available
    landmarksAvailable(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (!this.landmarksRenderer) {
            this.initLandmarksRenderer();
          }
          this.resizeCanvas();
          this.renderLandmarksAtTime(this.currentTime);
        });
      }
    },

    hideOrangeAlerts() {
      this.$nextTick(() => {
        this.rebuildTimelineMarkers();
        this.renderLandmarksAtTime(this.currentTime);
      });
    },

    coherenceTimelineActiveOnly() {
      this.rebuildTimelineMarkers();
    },

    volume(val) {
      const video = this.$refs.videoElement;
      if (video) {
        video.volume = Math.max(0, Math.min(1, Number(val) / 100));
      }
    },
  },

  created() {
    // Debounced render for smooth playback
    this.debouncedRenderLandmarks = this.debounce((time) => {
      this.renderLandmarksAtTime(time);
    }, 16);
    
    // Debounced render with prefetch for next window
    // Increased debounce to 200ms to prevent too many prefetch requests and duplicate calls
    this.debouncedRenderLandmarksWithPrefetch = this.debounce((time) => {
      this.renderLandmarksAtTime(time);
      // Prefetch next window during playback (only if playing)
      if (this.isPlaying) {
        this.prefetchNextWindow({ analysisId: this.analysisId, currentTime: time });
      }
    }, 200); // Increased to 200ms to reduce prefetch frequency and prevent duplicate requests
  },

  mounted() {
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
    window.addEventListener('keydown', this.onGlobalSpaceKeydown);

    this.$nextTick(() => {
      this.initLandmarksRenderer();
      this.setupResizeObserver();
      this.syncTimelineTrackWidth();
    });
  },

  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    window.removeEventListener('keydown', this.onGlobalSpaceKeydown);
    if (this.coherenceTimelineRaf) {
      cancelAnimationFrame(this.coherenceTimelineRaf);
      this.coherenceTimelineRaf = null;
    }
    if (this.timelineWidthRaf) {
      cancelAnimationFrame(this.timelineWidthRaf);
      this.timelineWidthRaf = null;
    }
    if (this.timelineHoverRaf) {
      cancelAnimationFrame(this.timelineHoverRaf);
      this.timelineHoverRaf = null;
    }
    if (this.playbackFeedbackTimeout) {
      clearTimeout(this.playbackFeedbackTimeout);
      this.playbackFeedbackTimeout = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },

  methods: {
    ...mapActions('analysisData', [
      'fetchLandmarksWindow',
      'ensureLandmarksAtTime',
      'warmLandmarksAroundTime',
      'prefetchNextWindow',
    ]),

    hasSemanticBlocksTimeline() {
      return (
        this.analysisViewMode === 'coherence' &&
        Array.isArray(this.coherenceTimelineBlocks) &&
        this.coherenceTimelineBlocks.length > 0
      );
    },

    async onVideoLoaded() {
      const video = this.$refs.videoElement;
      if (video && isFinite(video.duration)) {
        this.duration = video.duration;
        this.isLoading = false;
        this.hasError = false;
        this.rebuildTimelineMarkers();
        this.syncTimelineTrackWidth();
        video.volume = Math.max(0, Math.min(1, Number(this.volume) / 100));

        // Start loading landmarks for time 0 in background (non-blocking)
        this.ensureLandmarksAtTime({ 
          analysisId: this.analysisId, 
          timeSeconds: 0,
          isManual: this.isManualAnalysis,
        }).catch(() => {
          // Silently ignore - will retry on next render
        });
        
        // Start loading all landmarks windows in background
        this.startLoadingAllLandmarksWindows();
        
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.resizeCanvas();
            // Render landmarks at time 0 if available
            if (this.landmarksAvailable) {
              this.renderLandmarksAtTime(0);
            }
          });
        });
      }
    },

    onLoadStart() {
      this.isLoading = true;
    },

    onCanPlay() {
      this.isLoading = false;
      this.hasError = false;
    },

    onWaiting() {
      this.isLoading = true;
    },

    onVideoError(event) {
      console.error('Video error:', event);
      this.hasError = true;
      this.isLoading = false;
    },

    retryVideo() {
      const video = this.$refs.videoElement;
      if (video) {
        this.hasError = false;
        this.isLoading = true;
        video.load();
      }
    },

    onTimeUpdate() {
      const video = this.$refs.videoElement;
      if (video) {
        this.currentTime = video.currentTime;
      }
    },

    onVideoPlay() {
      this.isPlaying = true;
    },

    onVideoPause() {
      this.isPlaying = false;
    },

    togglePlayPause() {
      const video = this.$refs.videoElement;
      if (!video || this.hasError) return;

      if (video.paused) {
        // Prevent immediate re-pause from red-alert guard right after a user-initiated play.
        this.suppressAutoPauseUntilMs = Date.now() + 2500;
        this.triggerPlaybackFeedback('mdi-play');
        video.play();
      } else {
        this.triggerPlaybackFeedback('mdi-pause');
        video.pause();
      }

    },
    triggerPlaybackFeedback(iconName) {
      this.playbackFeedbackIcon = iconName;
      this.showPlaybackFeedback = false;
      if (this.playbackFeedbackTimeout) {
        clearTimeout(this.playbackFeedbackTimeout);
      }
      this.$nextTick(() => {
        this.showPlaybackFeedback = true;
        this.playbackFeedbackTimeout = setTimeout(() => {
          this.showPlaybackFeedback = false;
          this.playbackFeedbackTimeout = null;
        }, 250);
      });
    },
    onSpaceToggle(event) {
      const target = event && event.target ? event.target : null;
      if (!target) {
        this.togglePlayPause();
        return;
      }
      const tagName = String(target.tagName || '').toLowerCase();
      const isEditable =
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        target.isContentEditable;
      if (isEditable) return;
      this.togglePlayPause();
    },
    onGlobalSpaceKeydown(event) {
      if (!event) return;
      const isSpace = event.code === 'Space' || event.key === ' ';
      if (!isSpace) return;
      this.onSpaceToggle(event);
      event.preventDefault();
    },

    async jumpToTime(seconds) {
      const video = this.$refs.videoElement;
      if (!video || !isFinite(seconds) || isNaN(seconds)) return;
      
      // Ensure landmarks are loaded for the target time
      await this.ensureLandmarksForTime(seconds);
      
      video.currentTime = seconds;
    },

    async onTimelineClick(event) {
      event.stopPropagation();

      if (this.hasError) return;

      const video = this.$refs.videoElement;
      if (!video) return;

      const videoDuration = this.duration || video.duration;
      if (!videoDuration || isNaN(videoDuration) || !isFinite(videoDuration)) {
        return;
      }

      const timeline = event.currentTarget.querySelector('.timeline-track');
      if (!timeline) return;

      const rect = timeline.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      const newTime = percent * videoDuration;

      if (!isFinite(newTime) || isNaN(newTime)) return;

      // Report-missing flow: capture the time instead of seeking.
      if (this.reportMissingActive) {
        this.$emit('pick-timestamp', newTime);
        return;
      }

      // Ensure landmarks are loaded for the target time before seeking
      await this.ensureLandmarksForTime(newTime);
      video.currentTime = newTime;
    },
    onTimelineVisualHover(event) {
      if (!Number.isFinite(this.duration) || this.duration <= 0) return;
      this.pendingTimelineHoverClientX = event.clientX;
      if (this.timelineHoverRaf) return;
      this.timelineHoverRaf = requestAnimationFrame(() => {
        this.timelineHoverRaf = null;
        const track = this.$refs.timelineTrack || this.$refs.timelineVisualStack;
        if (!track) return;
        const rect = track.getBoundingClientRect();
        if (!rect.width) return;
        const ratio = this.clamp01((this.pendingTimelineHoverClientX - rect.left) / rect.width);
        const nextHoverTime = ratio * this.duration;
        const minDeltaSeconds = this.duration / Math.max(1, rect.width);
        if (
          Number.isFinite(this.timelineHoverTime) &&
          Math.abs(nextHoverTime - this.timelineHoverTime) < minDeltaSeconds
        ) {
          this.isTimelineHovering = true;
          return;
        }
        this.isTimelineHovering = true;
        this.timelineHoverTime = nextHoverTime;
      });
    },
    onTimelineVisualLeave() {
      this.isTimelineHovering = false;
      this.timelineHoverTime = null;
      this.pendingTimelineHoverClientX = null;
      if (this.timelineHoverRaf) {
        cancelAnimationFrame(this.timelineHoverRaf);
        this.timelineHoverRaf = null;
      }
    },
    syncTimelineTrackWidth() {
      if (this.timelineWidthRaf) {
        cancelAnimationFrame(this.timelineWidthRaf);
        this.timelineWidthRaf = null;
      }
      this.timelineWidthRaf = requestAnimationFrame(() => {
        this.timelineWidthRaf = null;
        const track = this.$refs.timelineTrack;
        if (!track || typeof track.getBoundingClientRect !== 'function') return;
        const width = track.getBoundingClientRect().width;
        const prevWidth = this.timelineTrackWidthPx;
        this.timelineTrackWidthPx =
          Number.isFinite(width) && width > 0 ? width : null;
        if (this.timelineTrackWidthPx !== prevWidth) {
          this.rebuildTimelineMarkers();
        }
      });
    },

    startDrag(event) {
      this.isDragging = true;
      const video = this.$refs.videoElement;
      if (video && !video.paused) {
        this.wasPlayingBeforeDrag = true;
        video.pause();
      } else {
        this.wasPlayingBeforeDrag = false;
      }
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
      event.preventDefault();
      event.stopPropagation();
    },

    onDrag(event) {
      if (!this.isDragging) return;

      const video = this.$refs.videoElement;
      if (!video) return;

      const videoDuration = this.duration || video.duration;
      if (!videoDuration || !isFinite(videoDuration)) return;

      let timeline = null;
      const section = event.target.closest('.timeline-section');
      if (section) {
        timeline = section.querySelector('.timeline-track');
      }
      if (!timeline && this.$refs.playerShell) {
        timeline = this.$refs.playerShell.querySelector('.timeline-track');
      }
      if (!timeline) return;

      const rect = timeline.getBoundingClientRect();
      const percent = this.clamp01((event.clientX - rect.left) / rect.width);
      const newTime = percent * videoDuration;

      if (isFinite(newTime) && !isNaN(newTime)) {
        // Update currentTime to show visual feedback while dragging
        this.currentTime = newTime;
        video.currentTime = newTime;
      }
    },

    async stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
      
      const video = this.$refs.videoElement;
      if (video) {
        // Ensure landmarks are loaded for the new position
        await this.ensureLandmarksForTime(video.currentTime);
        
        if (this.wasPlayingBeforeDrag) {
          video.play();
        }
      }
      this.wasPlayingBeforeDrag = false;
    },

    changePlaybackSpeed(speed) {
      const video = this.$refs.videoElement;
      if (video) {
        video.playbackRate = speed;
        this.playbackRate = speed;
      }
    },

    toggleMute() {
      const video = this.$refs.videoElement;
      if (video) {
        this.isMuted = !this.isMuted;
        video.muted = this.isMuted;
      }
    },

    toggleFullscreen() {
      const container = this.$refs.playerShell;
      if (!container) return;

      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        if (container.requestFullscreen) {
          container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
      }
    },

    onVolumeInput() {
      const video = this.$refs.videoElement;
      if (video) {
        video.volume = Math.max(0, Math.min(1, Number(this.volume) / 100));
      }
    },

    onFullscreenChange() {
      this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
      this.syncTimelineTrackWidth();
    },

    onToggleAnalysisViewMode() {
      this.$emit('toggle-analysis-view-mode');
    },

    onToggleOverlays() {
      this.$emit('toggle-overlays');
    },

    onMouseMove() {},

    onMouseLeave() {},

    onVideoClick(event) {
      if (
        event.target.closest('.video-overlay-slot') ||
        event.target.closest('.video-player-footer') ||
        event.target.closest('.play-pause-btn')
      ) {
        return;
      }

      const now = Date.now();
      if (now - Number(this.lastVideoSurfaceToggleAtMs || 0) < 260) {
        return;
      }
      this.lastVideoSurfaceToggleAtMs = now;

      this.togglePlayPause();
    },

    rebuildTimelineMarkers() {
      this.semanticBlockMarkers = [];
      this.coherenceDipGraphSamples = [];

      const useSemanticBlocks = this.hasSemanticBlocksTimeline();
      if (useSemanticBlocks) {
        this.buildSemanticBlockMarkers();
        this.buildCoherenceDipGraphFromBlocks();
        this.gestureMarkers = [];
        return;
      }

      const useCoherence =
        this.analysisViewMode === 'coherence' &&
        Array.isArray(this.coherenceTimelineAlerts) &&
        this.coherenceTimelineAlerts.length > 0;
      if (useCoherence) {
        this.buildCoherenceTimelineMarkers();
        return;
      }
      this.buildGestureMarkers();
    },

    coherenceBandColor(percent) {
      return coherenceBandHex(percent, this.semaphoreThresholdGood, this.semaphoreThresholdWarn);
    },
    clamp01(value) {
      return Math.max(0, Math.min(1, Number(value) || 0));
    },
    timelineRatio(seconds) {
      if (!Number.isFinite(this.duration) || this.duration <= 0) return 0;
      return this.clamp01(Number(seconds) / this.duration);
    },
    timelinePercentFromRatio(ratio) {
      return `${this.clamp01(ratio) * 100}%`;
    },
    timelineLeftPercent(seconds) {
      return this.timelinePercentFromRatio(this.timelineRatio(seconds));
    },
    timelineThumbLeftPercent(seconds) {
      const ratio = this.timelineRatio(seconds);
      const width = Number(this.timelineTrackWidthPx);
      if (!Number.isFinite(width) || width <= 0) return this.timelinePercentFromRatio(ratio);
      const half = 8; // .timeline-thumb width / 2
      const edgeRatio = this.clamp01(half / width);
      return this.timelinePercentFromRatio(Math.max(edgeRatio, Math.min(1 - edgeRatio, ratio)));
    },
    timelineMarkerLeftPercent(seconds) {
      const ratio = this.timelineRatio(seconds);
      const width = Number(this.timelineTrackWidthPx);
      if (!Number.isFinite(width) || width <= 0) return this.timelinePercentFromRatio(ratio);
      const half = 6; // default marker width / 2
      const edgeRatio = this.clamp01(half / width);
      return this.timelinePercentFromRatio(Math.max(edgeRatio, Math.min(1 - edgeRatio, ratio)));
    },
    timelineBlockStyle(block) {
      const startSeconds = Number(block?.startSeconds);
      const lengthSeconds = Number(block?.lengthSeconds);
      const endSeconds = startSeconds + lengthSeconds;
      const startRatio = this.timelineRatio(startSeconds);
      const endRatio = this.timelineRatio(endSeconds);
      const left = this.timelinePercentFromRatio(startRatio);
      const width = this.timelinePercentFromRatio(Math.max(0, endRatio - startRatio));
      return {
        left,
        width,
        '--block-color': block?.color,
      };
    },
    timelinePreviewStyle() {
      const from = this.timelineRatio(this.currentTime);
      const to = this.timelineRatio(this.timelineHoverTime);
      const leftRatio = Math.min(from, to);
      const widthRatio = Math.max(0, to - from);
      return {
        left: this.timelinePercentFromRatio(leftRatio),
        width: this.timelinePercentFromRatio(widthRatio),
        background: this.hoverPreviewColor,
      };
    },
    colorWithOpacity(color, opacity = 1) {
      const safeOpacity = Math.max(0, Math.min(1, Number(opacity) || 1));
      const value = String(color || '').trim();
      const hexMatch = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
      if (hexMatch) {
        const raw = hexMatch[1];
        const hex =
          raw.length === 3
            ? raw
                .split('')
                .map((ch) => `${ch}${ch}`)
                .join('')
            : raw;
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r},${g},${b},${safeOpacity})`;
      }
      const rgbMatch = value.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map((part) => Number(part.trim()));
        if (parts.length >= 3 && parts.slice(0, 3).every((n) => Number.isFinite(n))) {
          return `rgba(${parts[0]},${parts[1]},${parts[2]},${safeOpacity})`;
        }
      }
      return value;
    },

    blockAggregateCoherence(block) {
      return blockScoreForCoherenceMode(block, this.coherenceScoreMode);
    },
    coherenceTone(percent) {
      const p = Number(percent);
      const good = Number.isFinite(Number(this.semaphoreThresholdGood))
        ? Number(this.semaphoreThresholdGood)
        : 80;
      const warn = Number.isFinite(Number(this.semaphoreThresholdWarn))
        ? Number(this.semaphoreThresholdWarn)
        : 50;
      if (!Number.isFinite(p)) return 'success';
      if (p >= good) return 'success';
      if (p >= warn) return 'warning';
      return 'error';
    },
    defaultLandmarkColors() {
      return {
        faceRegions: {
          brows: 'success',
          lips: 'success',
          nose: 'success',
          eyes: 'success',
          mouth: 'success',
        },
        fullHead: 'success',
        hands: 'success',
      };
    },
    inferRegionFromGestureAlertItem(item) {
      const region = String(item?.region || '').toLowerCase();
      if (['brows', 'lips', 'nose', 'eyes', 'mouth'].includes(region)) return region;
      if (region === 'hands') return 'hands';
      if (region === 'full_head') return 'full_head';

      const category = String(item?.category || '').toLowerCase();
      if (category.includes('touch') || category.includes('hand')) return 'hands';
      if (category.includes('head')) return 'full_head';
      return 'full_head';
    },
    buildGestureLandmarkVisualState(gesture) {
      const colors = this.defaultLandmarkColors();
      if (!gesture) return { colors, overlay: null };

      const { alerts = [], warnings = [] } = detectAlerts(gesture) || {};
      const effectiveWarnings = this.hideOrangeAlerts ? [] : warnings;
      const severity = alerts.length > 0 ? 'error' : effectiveWarnings.length > 0 ? 'warning' : 'success';
      const candidates = alerts.length > 0 ? alerts : effectiveWarnings;
      if (!Array.isArray(candidates) || candidates.length === 0) {
        return { colors, overlay: null };
      }

      const sorted = [...candidates].sort((a, b) => Number(b?.magnitude || 0) - Number(a?.magnitude || 0));
      const primary = sorted[0];
      const region = this.inferRegionFromGestureAlertItem(primary);
      if (region === 'hands') {
        colors.hands = severity;
      } else if (region === 'full_head') {
        colors.fullHead = severity;
      } else if (colors.faceRegions[region] != null) {
        colors.faceRegions[region] = severity;
        if (region === 'lips') colors.faceRegions.mouth = severity;
        if (region === 'mouth') colors.faceRegions.lips = severity;
      } else {
        colors.fullHead = severity;
      }

      const label = String(primary?.label || primary?.type || '').trim();
      return {
        colors,
        overlay: label ? { label, tone: severity } : null,
      };
    },
    buildSemanticBoundingBoxVisualState(seconds) {
      if (this.analysisViewMode !== 'coherence') {
        return { boxColors: null, overlay: null };
      }

      const activeBlock = this.activeSemanticBlockAt(seconds);
      if (!activeBlock) {
        return { boxColors: null, overlay: null };
      }
      const activeAlerts = this.activeCoherenceAlertsForBlockAt(activeBlock, seconds);
      const primaryAlert = this.selectPrimaryCoherenceAlert(activeAlerts);
      const score = primaryAlert
        ? Number(primaryAlert?.coherence_after ?? primaryAlert?.coherenceAfter)
        : this.blockAggregateCoherence(activeBlock);

      const hasDetection =
        !!primaryAlert ||
        this.contributingAlertIds(activeBlock).length > 0 ||
        Number(activeBlock?.aggregate_alert_count ?? activeBlock?.aggregateAlertCount) > 0;
      const tone = this.coherenceTone(score);
      const boxColors = primaryAlert
        ? this.buildCoherenceLandmarkColors(primaryAlert, tone)
        : hasDetection
          ? {
            ...this.defaultLandmarkColors(),
            fullHead: tone,
          }
          : this.defaultLandmarkColors();
      const label = Number.isFinite(score) ? `${Math.round(score)}%` : '';
      return {
        boxColors,
        overlay: {
          label,
          tone,
          semanticBoundingBoxes: true,
          boxColors,
        },
      };
    },
    activeSemanticBlockAt(seconds) {
      const blocks = Array.isArray(this.coherenceTimelineBlocks) ? this.coherenceTimelineBlocks : [];
      if (!blocks.length) return null;
      const tMs = Number(seconds) * 1000;
      if (!Number.isFinite(tMs)) return null;
      const active = blocks.filter((b) => {
        const start = Number(b?.start_time_ms);
        const end = Number(b?.end_time_ms);
        return Number.isFinite(start) && Number.isFinite(end) && tMs >= start && tMs < end;
      });
      if (!active.length) return null;
      active.sort((a, b) => {
        const as = this.blockAggregateCoherence(a);
        const bs = this.blockAggregateCoherence(b);
        return Number(as) - Number(bs);
      });
      return active[0];
    },
    blockContributingAlertIds(block) {
      const ids = block?.contributing_alert_ids ?? block?.contributingAlertIds;
      if (!Array.isArray(ids)) return new Set();
      return new Set(ids.map((id) => String(id)));
    },
    activeCoherenceAlertsForBlockAt(block, seconds) {
      const alerts = Array.isArray(this.coherenceTimelineAlerts) ? this.coherenceTimelineAlerts : [];
      if (!block || !alerts.length) return [];
      const ids = this.blockContributingAlertIds(block);
      if (!ids.size) return [];
      return alerts.filter((a) => {
        const id = a?.alert_id ?? a?.alertId;
        if (id == null || !ids.has(String(id))) return false;
        return this.isCoherenceAlertActiveAt(a, seconds);
      });
    },
    selectPrimaryCoherenceAlert(alerts) {
      if (!Array.isArray(alerts) || !alerts.length) return null;
      const ranked = [...alerts];
      ranked.sort((a, b) => {
        const ta = Number(a?.coherence_after);
        const tb = Number(b?.coherence_after);
        if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
        const tsA = Number(a?.gesture_timestamp_ms);
        const tsB = Number(b?.gesture_timestamp_ms);
        if (Number.isFinite(tsA) && Number.isFinite(tsB)) return tsA - tsB;
        return 0;
      });
      return ranked[0];
    },
    wordsDia() {
      const list = this.coherenceSemanticModes?.diarization?.words;
      if (!Array.isArray(list)) return [];
      return list
        .slice()
        .sort((a, b) => {
          const ai = Number(a?.word_index);
          const bi = Number(b?.word_index);
          if (Number.isFinite(ai) && Number.isFinite(bi) && ai !== bi) return ai - bi;
          return Number(a?.start_time_ms) - Number(b?.start_time_ms);
        });
    },
    blocksPerAlert() {
      const list = this.coherenceSemanticModes?.diarization?.blocks_per_alert;
      return Array.isArray(list) ? list : [];
    },
    contributingAlertIds(block) {
      const raw = block?.contributing_alert_ids ?? block?.contributingAlertIds;
      return Array.isArray(raw) ? raw.map(String) : [];
    },
    alertIndexById() {
      const map = new Map();
      const list = Array.isArray(this.coherenceTimelineAlerts) ? this.coherenceTimelineAlerts : [];
      list.forEach((a) => {
        const id = a?.alert_id ?? a?.alertId;
        if (id != null && !map.has(String(id))) map.set(String(id), a);
      });
      return map;
    },
    currentWordAt(seconds) {
      const ms = Math.max(0, Math.round(Number(seconds) * 1000));
      const words = this.wordsDia();
      if (!Array.isArray(words) || !words.length) return null;

      let lo = 0;
      let hi = words.length - 1;
      let best = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const s = Number(words[mid]?.start_time_ms);
        if (!Number.isFinite(s)) {
          hi = mid - 1;
          continue;
        }
        if (s <= ms) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      if (best < 0) return null;

      for (let i = best; i >= 0; i -= 1) {
        const w = words[i];
        const s = Number(w?.start_time_ms);
        const e = Number(w?.end_time_ms);
        if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
        if (ms >= s && ms <= e) {
          return w;
        }
        if (e < ms) break;
      }
      return null;
    },
    currentWordIndexAt(seconds) {
      const word = this.currentWordAt(seconds);
      if (!word) return null;
      const idx = Number(word?.word_index);
      return Number.isFinite(idx) ? idx : null;
    },
    currentWordPills(seconds) {
      const word = this.currentWordAt(seconds);
      if (!word) return [];
      const idx = Number(word?.word_index);
      if (!Number.isFinite(idx)) return [];

      const activeBlock = this.activeSemanticBlockAt(seconds);
      if (!activeBlock) return [];

      const highlightRaw = activeBlock?.highlight_word_indexes ?? activeBlock?.highlightWordIndexes;
      const blockWordIndexes = Array.isArray(highlightRaw)
        ? Array.from(
            new Set(highlightRaw.map((x) => Number(x)).filter((x) => Number.isFinite(x)))
          ).sort((a, b) => a - b)
        : [];
      if (!blockWordIndexes.length) return [];
      const blockWordSet = new Set(blockWordIndexes);

      const words = this.wordsDia();
      const wordByIndex = new Map();
      words.forEach((w) => {
        const wi = Number(w?.word_index);
        if (Number.isFinite(wi) && !wordByIndex.has(wi)) wordByIndex.set(wi, w);
      });

      const alertById = this.alertIndexById();
      const pills = [];
      const dedupe = new Set();
      const activeBlockAlertIds = this.contributingAlertIds(activeBlock);
      const perAlertBlocks = this.blocksPerAlert();

      activeBlockAlertIds.forEach((alertIdRaw) => {
        const alertId = String(alertIdRaw);
        if (!alertId) return;
        const perAlertBlock = perAlertBlocks.find((block) =>
          this.contributingAlertIds(block).includes(alertId)
        );
        const alert = alertById.get(alertId) || null;

        let displayWordIndex = null;
        const anchor = Number(perAlertBlock?.anchor_word_index ?? perAlertBlock?.anchorWordIndex);
        if (Number.isFinite(anchor) && blockWordSet.has(anchor)) {
          displayWordIndex = anchor;
        } else {
          const targetMs = Number(alert?.gesture_timestamp_ms ?? alert?.gestureTimestampMs);
          if (Number.isFinite(targetMs)) {
            let bestIdx = null;
            let bestDist = Number.POSITIVE_INFINITY;
            blockWordIndexes.forEach((wi) => {
              const blockWord = wordByIndex.get(wi);
              const ws = Number(blockWord?.start_time_ms);
              if (!Number.isFinite(ws)) return;
              const dist = Math.abs(ws - targetMs);
              if (dist < bestDist) {
                bestDist = dist;
                bestIdx = wi;
              }
            });
            if (Number.isFinite(bestIdx)) displayWordIndex = bestIdx;
          }
        }
        if (!Number.isFinite(displayWordIndex)) {
          displayWordIndex = Number(blockWordIndexes[0]);
        }
        if (displayWordIndex !== idx) return;

        const key = `${String(activeBlock?.block_id ?? activeBlock?.blockId ?? '')}|${alertId}`;
        if (dedupe.has(key)) return;
        dedupe.add(key);

        const subtractionRaw = alert ? this.alertSubtractionPercent(alert) : null;
        const subtraction =
          Number.isFinite(Number(subtractionRaw))
            ? Math.abs(Number(subtractionRaw))
            : Math.max(
                0,
                100 -
                  Number(
                    perAlertBlock?.score_resta_computada_after ??
                      perAlertBlock?.scoreRestaComputadaAfter
                  )
              );
        pills.push({
          alertId,
          alert,
          block: perAlertBlock || activeBlock,
          subtraction: Number.isFinite(Number(subtraction)) ? Number(subtraction) : null,
        });
      });

      pills.sort((a, b) => {
        const at = Number(a?.alert?.gesture_timestamp_ms ?? a?.alert?.gestureTimestampMs);
        const bt = Number(b?.alert?.gesture_timestamp_ms ?? b?.alert?.gestureTimestampMs);
        if (Number.isFinite(at) && Number.isFinite(bt) && at !== bt) return at - bt;
        const aa = String(a?.alertId ?? '');
        const bb = String(b?.alertId ?? '');
        return aa.localeCompare(bb);
      });

      return pills;
    },
    subtractionForPills(pills) {
      const safe = Array.isArray(pills) ? pills : [];
      if (!safe.length) return null;

      if (safe.length === 1) {
        const single = Number(safe[0]?.subtraction);
        return Number.isFinite(single) ? Math.abs(single) : null;
      }

      if (this.coherenceScoreMode === 'computed_subtraction') {
        const total = safe.reduce((sum, pill) => {
          const subtraction = Number(pill?.subtraction);
          if (!Number.isFinite(subtraction)) return sum;
          return sum + Math.abs(subtraction);
        }, 0);
        return Math.min(100, total);
      }

      const withCoherence = safe
        .map((pill) => ({
          pill,
          coherence: Number(
            pill?.alert?.coherence_after ?? pill?.alert?.coherenceAfter
          ),
        }))
        .filter((row) => Number.isFinite(row.coherence))
        .sort((a, b) => a.coherence - b.coherence);

      if (withCoherence.length) {
        const subtraction = Number(withCoherence[0]?.pill?.subtraction);
        return Number.isFinite(subtraction) ? Math.abs(subtraction) : null;
      }

      const reductions = safe
        .map((pill) => Number(pill?.subtraction))
        .filter((n) => Number.isFinite(n));
      return reductions.length ? Math.max(...reductions.map((n) => Math.abs(n))) : null;
    },
    coherenceWordScoreLabel(pills) {
      const subtraction = this.subtractionForPills(pills);
      if (!Number.isFinite(subtraction)) return '';
      return `-${Math.round(Math.abs(subtraction))}%`;
    },
    isHandsRegionAlert(alert) {
      const target = this.inferLandmarkTargetFromCoherenceAlert(alert);
      return target?.type === 'hands';
    },
    splitPillsByRegion(pills) {
      const safe = Array.isArray(pills) ? pills : [];
      const byRegion = {
        head: [],
        hands: [],
      };
      safe.forEach((pill) => {
        if (this.isHandsRegionAlert(pill?.alert)) {
          byRegion.hands.push(pill);
          return;
        }
        byRegion.head.push(pill);
      });
      return byRegion;
    },
    alertSubtractionPercent(alert) {
      const before = Number(alert?.coherence_before ?? alert?.coherenceBefore);
      const after = Number(alert?.coherence_after ?? alert?.coherenceAfter);
      if (Number.isFinite(before) && Number.isFinite(after)) {
        return before - after;
      }
      const meta = alert?.metadata;
      const fallback = Number(
        meta?.score_resta_computada ??
          meta?.scoreRestaComputada ??
          meta?.subtraction ??
          meta?.delta
      );
      return Number.isFinite(fallback) ? fallback : null;
    },
    inferLandmarkTargetFromCoherenceAlert(alert) {
      const tokens = [
        alert?.gesture_name,
        alert?.rule_label,
        alert?.rule_id,
        alert?.metadata?.source_value,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (tokens.includes('brow')) return { type: 'face_region', region: 'brows' };
      if (tokens.includes('lip')) return { type: 'face_region', region: 'lips' };
      if (tokens.includes('nose')) return { type: 'face_region', region: 'nose' };
      if (tokens.includes('eye')) return { type: 'face_region', region: 'eyes' };
      if (tokens.includes('mouth')) return { type: 'face_region', region: 'mouth' };
      if (
        tokens.includes('touch') ||
        tokens.includes('hand') ||
        tokens.includes('finger') ||
        tokens.includes('index') ||
        tokens.includes('pinch')
      ) {
        return { type: 'hands' };
      }
      if (
        tokens.includes('nodd') ||
        tokens.includes('shak') ||
        tokens.includes('head') ||
        tokens.includes('gaze') ||
        tokens.includes('direction') ||
        tokens.includes('shrug') ||
        tokens.includes('tilt')
      ) {
        return { type: 'full_head' };
      }
      return { type: 'full_head' };
    },
    buildCoherenceLandmarkColors(activeAlert, pointTone) {
      const colors = this.defaultLandmarkColors();
      if (!activeAlert) return colors;
      const target = this.inferLandmarkTargetFromCoherenceAlert(activeAlert);
      if (target.type === 'face_region' && target.region && colors.faceRegions[target.region] != null) {
        colors.faceRegions[target.region] = pointTone;
        if (target.region === 'lips') colors.faceRegions.mouth = pointTone;
        if (target.region === 'mouth') colors.faceRegions.lips = pointTone;
        return colors;
      }
      if (target.type === 'hands') {
        colors.hands = pointTone;
        return colors;
      }
      colors.fullHead = pointTone;
      return colors;
    },
    buildSemanticBlockMarkers() {
      const blocks = Array.isArray(this.coherenceTimelineBlocks) ? this.coherenceTimelineBlocks : [];
      this.semanticBlockMarkers = blocks
        .map((b, index) => {
          const startMs = Number(b?.start_time_ms);
          const endMs = Number(b?.end_time_ms);
          if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;

          const startSeconds = startMs / 1000;
          const lengthSeconds = Math.max(0.02, (endMs - startMs) / 1000);
          const aggregate = this.blockAggregateCoherence(b);
          const aggregateLabel = Number.isFinite(aggregate) ? `${Math.round(aggregate)}%` : '—';
          const color = this.colorWithOpacity(this.coherenceBandColor(aggregate), 0.86);

          return {
            key: b?.block_id || `semantic-block-${index}`,
            startSeconds,
            lengthSeconds,
            color,
            aggregateLabel,
            description: `${this.formatTimeMarker(startSeconds)} - ${this.formatTimeMarker(startSeconds + lengthSeconds)} | ${aggregateLabel}`,
          };
        })
        .filter(Boolean);
    },

    buildCoherenceDipGraphFromBlocks() {
      const blocks = Array.isArray(this.coherenceTimelineBlocks) ? this.coherenceTimelineBlocks : [];
      if (!blocks.length) {
        this.coherenceDipGraphSamples = [];
        return;
      }

      const pxWidth = Number(this.timelineTrackWidthPx);
      const sampleCount = Number.isFinite(pxWidth) && pxWidth > 0
        ? Math.max(240, Math.round(pxWidth))
        : 240;
      const base = 100;
      const raw = Array.from({ length: sampleCount }, () => base);

      blocks.forEach((b) => {
        const startMs = Number(b?.start_time_ms);
        const endMs = Number(b?.end_time_ms);
        if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return;

        const aggregate = this.blockAggregateCoherence(b);
        if (!Number.isFinite(aggregate)) return;

        const startSec = startMs / 1000;
        const endSec = endMs / 1000;
        const startIdx =
          this.duration > 0
            ? Math.max(0, Math.min(sampleCount - 1, Math.floor((startSec / this.duration) * (sampleCount - 1))))
            : 0;
        const endIdx =
          this.duration > 0
            ? Math.max(startIdx, Math.min(sampleCount - 1, Math.ceil((endSec / this.duration) * (sampleCount - 1))))
            : startIdx;

        for (let i = startIdx; i <= endIdx; i += 1) {
          raw[i] = Math.min(raw[i], aggregate);
        }
      });

      const maxDipHeight = 62;
      this.coherenceDipGraphSamples = raw.map((value) => {
        const safe = Math.max(0, Math.min(100, Number(value)));
        const dip = (100 - safe) / 100;
        return {
          y: dip * maxDipHeight,
          color: this.coherenceBandColor(safe),
        };
      });
    },

    coherenceAlertAnchorSeconds(alert) {
      const ts = Number(alert?.gesture_timestamp_ms);
      if (Number.isFinite(ts) && ts >= 0) return ts / 1000;
      const range = alert?.gesture_time_range_ms;
      if (Array.isArray(range) && range.length >= 1) {
        const start = Number(range[0]);
        if (Number.isFinite(start) && start >= 0) return start / 1000;
      }
      return 0;
    },

    coherenceAlertWindowEndSeconds(alert) {
      return this.coherenceAlertAnchorSeconds(alert) + 0.75;
    },

    isCoherenceAlertActiveAt(alert, seconds) {
      const start = this.coherenceAlertAnchorSeconds(alert);
      const end = this.coherenceAlertWindowEndSeconds(alert);
      return seconds >= start && seconds < end;
    },

    buildCoherenceTimelineMarkers() {
      const alerts = this.coherenceTimelineAlerts;
      if (!Array.isArray(alerts) || alerts.length === 0) {
        this.gestureMarkers = [];
        return;
      }

      let source = alerts;
      if (this.coherenceTimelineActiveOnly) {
        const t = this.currentTime;
        source = alerts.filter((a) => this.isCoherenceAlertActiveAt(a, t));
      }

      this.gestureMarkers = source
        .map((a, index) => {
          let startMs = a.gesture_timestamp_ms;
          if (startMs == null && Array.isArray(a.gesture_time_range_ms) && a.gesture_time_range_ms.length > 0) {
            startMs = a.gesture_time_range_ms[0];
          }
          const seconds = Number(startMs) / 1000;
          const after = Number(a.coherence_after);
          /* Keep timeline marker tiers aligned with semaphore bands. */
          let alertLevel = 'bad';
          const good = Number.isFinite(Number(this.semaphoreThresholdGood))
            ? Number(this.semaphoreThresholdGood)
            : 80;
          const warn = Number.isFinite(Number(this.semaphoreThresholdWarn))
            ? Number(this.semaphoreThresholdWarn)
            : 50;
          if (Number.isFinite(after)) {
            if (after >= good) alertLevel = 'good';
            else if (after >= warn) alertLevel = 'warn';
            else alertLevel = 'bad';
          }
          const label = a.gesture_name || a.rule_id || 'Coherence';
          const descAfter = Number.isFinite(after) ? `${Math.round(after)}%` : '—';
          return {
            seconds,
            alertLevel,
            magnitude: Number.isFinite(after) ? 100 - after : 50,
            description: `${label} (${descAfter})`,
            key: a.alert_id || `va-${index}`,
          };
        })
        .filter((m) => Number.isFinite(m.seconds) && m.seconds >= 0);
    },

    buildGestureMarkers() {
      if (!this.gestureData || this.gestureData.length === 0) {
        this.gestureMarkers = [];
        return;
      }

      this.gestureMarkers = this.gestureData
        .map((g) => {
          const { alerts, warnings, totalMagnitude } = detectAlerts(g);
          const alertLevel = alerts.length > 0 ? 'alert' : warnings.length > 0 ? 'warning' : 'normal';
          const magnitude = totalMagnitude; // Use filtered magnitude from detectAlerts
          const seconds = this.parseTimeToSeconds(g.time);

          return {
            seconds,
            magnitude,
            level: alertLevel === 'alert' ? 'high' : alertLevel === 'warning' ? 'medium' : 'low',
            alertLevel: alertLevel, // Store original alert level
            data: g,
          };
        })
        .filter((marker) => {
          // Only show markers with magnitude > 0 (filtered by baseline)
          const shouldShow = marker.magnitude > 0 && (marker.level === 'high' || marker.level === 'medium');
          // Filter out orange (medium) alerts if hideOrangeAlerts is true
          if (this.hideOrangeAlerts && marker.level === 'medium') {
            return false;
          }
          return shouldShow;
        })
        .map((marker) => ({
          seconds: marker.seconds,
          alertLevel: marker.level === 'high' ? 'low' : 'medium', // Map to UI levels
          magnitude: marker.magnitude,
          description: `${this.formatTimeMarker(marker.seconds)} - ${marker.level === 'high' ? 'Alert' : 'Warning'} (${Math.round(marker.magnitude)}%)`,
        }));

    },

    calculateCoherenceScore(gestureData) {
      if (!gestureData) return 1;

      let alerts = 0;
      const total = 8;

      if (gestureData.brow_alert) alerts++;
      if (gestureData.lip_alert) alerts++;
      if (gestureData.nose_alert) alerts++;

      if (gestureData.alert === 'very_large') alerts += 2;
      else if (gestureData.alert === 'large') alerts++;

      if (gestureData.hands_alert) alerts++;
      if (gestureData.contempt_alert) alerts += 2;

      return 1 - alerts / total;
    },

    formatTimeMarker(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      const ms = Math.floor((seconds % 1) * 1000);
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(ms).padStart(3, '0')}`;
    },

    parseTimeToSeconds(timeString) {
      if (!timeString) return 0;
      const parts = timeString.split(':');
      if (parts.length !== 3) return 0;

      const minutes = parseInt(parts[0], 10) || 0;
      const seconds = parseInt(parts[1], 10) || 0;
      const milliseconds = parseInt(parts[2], 10) || 0;

      return minutes * 60 + seconds + milliseconds / 1000;
    },

    formatTime(seconds) {
      if (!seconds || !isFinite(seconds) || isNaN(seconds)) {
        return '00:00';
      }

      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    },

    formatTimeWithMs(seconds) {
      if (!Number.isFinite(seconds) || seconds < 0) return '00:00.000';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      const ms = Math.floor((seconds - Math.floor(seconds)) * 1000);
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
    },

    /**
     * Rendered rectangle of decoded video pixels for current object-fit mode
     * (coordinates relative to the video element content box).
     */
    getVideoObjectFitRect(video) {
      const elW = video.clientWidth;
      const elH = video.clientHeight;
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const objectFit = String(window.getComputedStyle(video).objectFit || 'contain').trim().toLowerCase();
      if (!elW || !elH) {
        return { x: 0, y: 0, w: 0, h: 0 };
      }
      if (!vw || !vh) {
        return { x: 0, y: 0, w: elW, h: elH };
      }
      const elRatio = elW / elH;
      const vidRatio = vw / vh;
      if (objectFit === 'fill') {
        return { x: 0, y: 0, w: elW, h: elH };
      }
      if (objectFit === 'none') {
        return {
          x: (elW - vw) / 2,
          y: (elH - vh) / 2,
          w: vw,
          h: vh,
        };
      }
      let cw;
      let ch;
      let cx;
      let cy;
      const useCover = objectFit === 'cover';
      if (useCover) {
        if (vidRatio > elRatio) {
          ch = elH;
          cw = elH * vidRatio;
          cy = 0;
          cx = (elW - cw) / 2;
        } else {
          cw = elW;
          ch = elW / vidRatio;
          cx = 0;
          cy = (elH - ch) / 2;
        }
      } else {
        if (vidRatio > elRatio) {
          cw = elW;
          ch = elW / vidRatio;
          cx = 0;
          cy = (elH - ch) / 2;
        } else {
          ch = elH;
          cw = elH * vidRatio;
          cy = 0;
          cx = (elW - cw) / 2;
        }
      }
      return { x: cx, y: cy, w: cw, h: ch };
    },

    resizeCanvas() {
      const video = this.$refs.videoElement;
      const canvas = this.$refs.landmarksCanvas;
      const videoWrapper = this.$refs.videoWrapper;

      if (video && canvas && videoWrapper) {
        const r = this.getVideoObjectFitRect(video);
        const displayWidth = r.w;
        const displayHeight = r.h;

        if (displayWidth <= 0 || displayHeight <= 0) {
          return;
        }

        if (
          this.lastCanvasSize.width === displayWidth &&
          this.lastCanvasSize.height === displayHeight &&
          this.lastCanvasSize.x === r.x &&
          this.lastCanvasSize.y === r.y
        ) {
          return;
        }

        const videoWidth = video.videoWidth || displayWidth;
        const videoHeight = video.videoHeight || displayHeight;

        canvas.width = videoWidth;
        canvas.height = videoHeight;

        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;

        const videoRect = video.getBoundingClientRect();
        const wrapperRect = videoWrapper.getBoundingClientRect();
        const baseLeft =
          videoRect.left -
          wrapperRect.left +
          video.clientLeft +
          r.x;
        const baseTop =
          videoRect.top - wrapperRect.top + video.clientTop + r.y;

        canvas.style.position = 'absolute';
        canvas.style.left = `${baseLeft}px`;
        canvas.style.top = `${baseTop}px`;
        canvas.style.maxWidth = `${displayWidth}px`;
        canvas.style.maxHeight = `${displayHeight}px`;

        this.lastCanvasSize = {
          width: displayWidth,
          height: displayHeight,
          x: r.x,
          y: r.y,
        };

        if (this.landmarksRenderer) {
          this.renderLandmarksAtTime(this.currentTime);
        }
      }
    },

    setupResizeObserver() {
      if (typeof ResizeObserver === 'undefined') {
        window.addEventListener('resize', this.debounce(() => {
          this.resizeCanvas();
          this.syncTimelineTrackWidth();
        }, 100));
        return;
      }

      const video = this.$refs.videoElement;
      if (!video) return;

      this.resizeObserver = new ResizeObserver((entries) => {
        this.debounce(() => {
          this.resizeCanvas();
          this.syncTimelineTrackWidth();
        }, 50)();
      });

      this.resizeObserver.observe(video);
      const wrapper = this.$refs.videoWrapper;
      if (wrapper) {
        this.resizeObserver.observe(wrapper);
      }
    },

    initLandmarksRenderer() {
      if (this.landmarksRenderer) {
        return;
      }
      const canvas = this.$refs.landmarksCanvas;
      if (canvas) {
        this.landmarksRenderer = new LandmarksRenderer(canvas);
      }
    },

    async renderLandmarksAtTime(currentTime) {
      if (!this.landmarksRenderer) {
        this.initLandmarksRenderer();
      }

      // Try to get landmarks from store first (fast path)
      let landmarksFrame = this.getLandmarksAtTime(this.analysisId, currentTime);
      if (landmarksFrame) {
        if (this.landmarksRenderer) {
          this.renderLandmarksFrame(currentTime, landmarksFrame);
        }
        return;
      }

      // If not in store, ensure landmarks are loaded for this time (will load from store if needed)
      // Use non-blocking approach: don't await, render when available
      try {
        const newLandmarksFrame = await this.ensureLandmarksAtTime({ 
          analysisId: this.analysisId, 
          timeSeconds: currentTime,
          isManual: this.isManualAnalysis,
        });
        
        if (newLandmarksFrame && this.landmarksRenderer) {
          this.renderLandmarksFrame(currentTime, newLandmarksFrame);
        } else {
          // If still no frame after loading, check store again (might have been loaded by background fetch)
          landmarksFrame = this.getLandmarksAtTime(this.analysisId, currentTime);
          if (landmarksFrame && this.landmarksRenderer) {
            this.renderLandmarksFrame(currentTime, landmarksFrame);
          }
          // Don't clear canvas if no landmarks - keep last rendered frame
        }
      } catch (err) {
        // Retry: Check store again in case data was loaded by another process
        landmarksFrame = this.getLandmarksAtTime(this.analysisId, currentTime);
        if (landmarksFrame && this.landmarksRenderer) {
          this.renderLandmarksFrame(currentTime, landmarksFrame);
        }
        // Don't clear canvas on error - keep last rendered frame
      }
    },

    renderLandmarksFrame(currentTime, landmarksFrame) {
      if (!this.landmarksRenderer) {
        return;
      }

      if (landmarksFrame) {
        const gesture = this.findGestureAtTime(currentTime);
        const visualState = this.buildGestureLandmarkVisualState(gesture);
        const semanticBoxState = this.buildSemanticBoundingBoxVisualState(currentTime);
        const overlay = semanticBoxState.overlay || (
          this.analysisViewMode === 'coherence'
            ? {
                showBoundingBoxes: false,
              }
            : visualState.overlay
        );

        this.landmarksRenderer.render(
          landmarksFrame,
          this.toggleOptions,
          visualState.colors,
          overlay
        );
      }
      // Don't clear canvas if no landmarks - keep last rendered frame visible
    },

    renderLandmarksAtIndex(index) {
      if (!this.landmarksRenderer) {
        this.initLandmarksRenderer();
        if (!this.landmarksRenderer) return;
      }

      if (!this.landmarksData || this.landmarksData.length === 0) {
        return;
      }

      const landmarksFrame = this.landmarksData[index];

      if (landmarksFrame) {
        this.resizeCanvas();

        // Get colors for current frame (if gesture data available)
        const landmarkColors = null; // In analyzing mode, we might not have gesture data yet
        this.landmarksRenderer.render(landmarksFrame, this.toggleOptions, landmarkColors, null);
      } else {
        this.landmarksRenderer.clear();
      }
    },

    /**
     * Find landmarks frame at a specific time
     * Uses the Vuex store which loads landmarks in windows
     */
    findLandmarksAtTime(seconds) {
      // Use the store getter to get landmarks at time
      return this.getLandmarksAtTime(this.analysisId, seconds);
    },
    
    /**
     * Ensure landmarks are loaded for a specific time (used during seek)
     * Pauses video while loading if necessary
     */
    async ensureLandmarksForTime(timeSeconds) {
      const windowIndex = this.getWindowIndex(timeSeconds);
      
      // If window is already loaded, return immediately
      if (this.hasLandmarksWindow(this.analysisId, windowIndex)) {
        return;
      }
      
      // Need to load the window - pause video and show loading
      const video = this.$refs.videoElement;
      const wasPlaying = video && !video.paused;
      
      if (wasPlaying) {
        video.pause();
      }
      
      this.isLoadingLandmarksWindow = true;
      
      try {
        await this.warmLandmarksAroundTime({
          analysisId: this.analysisId,
          timeSeconds,
          isManual: this.isManualAnalysis,
          radius: 1,
        });
      } catch (error) {
        console.error('[VideoLandmarksPlayer] Error loading landmarks window:', error);
      } finally {
        this.isLoadingLandmarksWindow = false;
        
        // Resume playback if it was playing before
        if (wasPlaying && video) {
          video.play();
        }
      }
    },

    /**
     * Start loading all landmarks windows in background sequentially
     * Loads windows 0-10s, 10-20s, 20-30s, etc. until the end of the video
     * 
     * @deprecated - This method is disabled to prevent memory issues and excessive server requests.
     * Landmarks are now loaded on-demand via prefetch during video playback.
     */
    async startLoadingAllLandmarksWindows() {
      // DISABLED: Loading all windows at once causes memory issues for long videos
      // and creates too many simultaneous requests to the server.
      // Landmarks are now loaded on-demand via prefetch during playback.
      return;
    },

    checkForRedAlert(currentTime) {
      if (Date.now() < Number(this.suppressAutoPauseUntilMs || 0)) return;
      if (!this.gestureData || this.gestureData.length === 0) return;

      const gesture = this.findGestureAtTime(currentTime);
      if (!gesture) return;

      const { alerts } = detectAlerts(gesture);
      const alertLevel = alerts.length > 0 ? 'alert' : 'normal';

      // Pause if alert level is 'alert' (red)
      if (alertLevel === 'alert') {
        const gestureSeconds = this.parseTimeToSeconds(gesture.time);
        if (
          !this.isPausedByRedAlert ||
          (this.lastRedAlertTime && Math.abs(this.lastRedAlertTime - gestureSeconds) > 1)
        ) {
          this.pauseForRedAlert(gestureSeconds);
        }
      }
    },

    findGestureAtTime(seconds) {
      if (!this.gestureData || this.gestureData.length === 0) return null;

      let closestGesture = null;
      let minDiff = Infinity;

      // Tolerance window: gestures are valid within 0.5 seconds
      const tolerance = 0.5;

      for (const gesture of this.gestureData) {
        const gestureSeconds = this.parseTimeToSeconds(gesture.time);
        const diff = Math.abs(gestureSeconds - seconds);

        // If we're past the gesture time, use tolerance window
        if (gestureSeconds > seconds + tolerance) {
          break; // We're past all relevant gestures
        }

        if (diff < minDiff) {
          minDiff = diff;
          closestGesture = gesture;
        }
      }

      // CRITICAL: Only return gesture if it's within tolerance window
      // This prevents "sticky" alerts when video advances beyond available gesture data
      if (closestGesture && minDiff > tolerance) {
        return null; // No gesture found within tolerance
      }

      return closestGesture;
    },

    pauseForRedAlert(alertTime) {
      const video = this.$refs.videoElement;
      if (video && !video.paused) {
        video.pause();
        this.isPausedByRedAlert = true;
        this.lastRedAlertTime = alertTime;
      }
    },

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },
  },
};
</script>

<style scoped>
.video-landmarks-player {
  --video-chrome-bg: #2a2a2a;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
}

.video-landmarks-player:focus,
.video-landmarks-player:focus-visible {
  outline: none;
  box-shadow: none;
}

.video-wrapper {
  position: relative;
  width: 100%;
  flex: 1 1 0;
  /* Absolute-positioned <video> does not contribute intrinsic height; without a floor,
     flex-basis 0 + siblings (above/below slots + footer) can collapse this to 0px. */
  min-height: clamp(160px, 36vmin, 72vh);
  background: var(--video-chrome-bg);
  border-radius: 0;
  overflow: visible;
  display: flex;
  align-items: center;
  justify-content: center;
  contain: layout style;
  will-change: auto;
}

.video-container {
  background: var(--video-chrome-bg) !important;
  border-radius: 8px;
  overflow: hidden;
  margin: 0 !important;
  padding: 0 !important;
  height: 100%;
  min-height: 100%;
  max-height: 100%;
  align-self: stretch;
  min-height: 0;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  position: relative;
  z-index: 0;
  isolation: isolate;
}

.player-shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: var(--video-chrome-bg);
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  z-index: 0;
}

.video-overlay-slot {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4;
  pointer-events: none;
}

.video-above-video-slot,
.video-below-video-slot {
  flex-shrink: 0;
  position: relative;
  z-index: 5;
  pointer-events: auto;
}

.playback-feedback-overlay {
  position: absolute;
  inset: 0;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.playback-feedback-badge {
  width: 76px;
  height: 76px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale(1);
}

.playback-feedback-fade-enter-active,
.playback-feedback-fade-leave-active {
  transition: opacity 0.25s ease-out, transform 0.25s ease-out;
}

.playback-feedback-fade-enter,
.playback-feedback-fade-leave-to {
  opacity: 0;
}

.playback-feedback-fade-enter .playback-feedback-badge,
.playback-feedback-fade-leave-to .playback-feedback-badge {
  transform: scale(0.9);
}

.video-player-footer {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  background: var(--video-chrome-bg);
  border-top: none;
  padding-top: 0;
}

.footer-control-btn::before {
  opacity: 0.12;
}

.footer-text-btn {
  font-weight: 500;
  letter-spacing: 0.02em;
}

.timeline-section {
  padding: 10px 12px 4px;
  cursor: pointer;
  user-select: none;
}

.timeline-visual-stack {
  position: relative;
  width: 100%;
  overflow-x: clip;
  overflow-y: visible;
}

.timeline-hover-time-bubble {
  position: absolute;
  bottom: 100%;
  transform: translate(-50%, -8px);
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 4;
  font-variant-numeric: tabular-nums;
}

.timeline-hover-time-bubble::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 4px solid transparent;
  border-top-color: rgba(0, 0, 0, 0.85);
}

.timeline-hover-time-bubble--picking {
  background: #f9a825;
  color: #000;
}

.timeline-hover-time-bubble--picking::after {
  border-top-color: #f9a825;
}

.footer-controls-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 12px;
  min-height: 40px;
}

.footer-controls-left,
.footer-controls-center,
.footer-controls-right {
  display: flex;
  align-items: center;
  flex: 1 1 0;
}

.footer-controls-left {
  justify-content: flex-start;
}

.footer-controls-center {
  justify-content: center;
}

.footer-controls-right {
  justify-content: flex-end;
}

.time-display-footer {
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  min-width: 110px;
}

.volume-slider-inline {
  width: 100px;
  min-width: 80px;
}

/* Fill the stage; letterbox/pillarbox via object-fit */
.interview-video {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  margin: auto;
  object-fit: contain;
  object-position: center center;
  display: block;
  contain: layout style;
  backface-visibility: hidden;
  transform: translateZ(0);
}

.landmarks-canvas {
  position: absolute;
  pointer-events: none;
  z-index: 2;
  will-change: transform;
  contain: strict;
  backface-visibility: hidden;
  transform: translateZ(0);
  object-fit: contain;
  /* Position and size are set dynamically in resizeCanvas() */
}

.loading-overlay,
.error-overlay,
.analysis-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.analysis-overlay {
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  pointer-events: none;
}

.analysis-content {
  pointer-events: auto;
}

.analysis-content {
  text-align: center;
  padding: 20px;
}

.analysis-step-text {
  color: white;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
}

.analysis-hint-text {
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  max-width: 320px;
  margin: 0 auto;
}

.loading-text,
.error-text {
  color: white;
  margin-top: 16px;
  font-size: 16px;
}

.video-controls-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3) 0%,
    transparent 30%,
    transparent 70%,
    rgba(0, 0, 0, 0.8) 100%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 5;
}

.video-controls-overlay.show-controls {
  opacity: 1;
  pointer-events: auto;
}

.bottom-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px 16px;
  pointer-events: auto;
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.play-pause-control {
  display: flex;
  align-items: center;
}

.play-pause-btn {
  transition: transform 0.2s ease;
  min-width: 36px !important;
  width: 36px !important;
  height: 36px !important;
}

.play-pause-btn:hover {
  transform: scale(1.1);
}

.progress-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.timeline-container {
  width: 100%;
  padding: 8px 0;
  cursor: pointer;
  user-select: none;
}

.custom-timeline {
  width: 100%;
  height: 6px;
  background: #3f3f3f;
  border-radius: 0;
  position: relative;
  overflow-x: clip;
  overflow-y: visible;
  transition: height 0.2s ease;
}

.timeline-container:hover .custom-timeline {
  height: 8px;
}

.timeline-track {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 0;
  overflow-x: clip;
  overflow-y: visible;
}

.coherence-block-marker {
  position: absolute;
  top: 0;
  transform: none;
  height: 100%;
  border-radius: 0;
  border: none;
  cursor: pointer;
  z-index: 1;
  box-shadow: none;
  display: block;
  padding: 0;
  overflow: hidden;
  background-color: var(--block-color);
}

/* While a pick mode is active, clicks must reach the track instead of marker jump handlers. */
.timeline-track--picking .coherence-block-marker,
.timeline-track--picking .gesture-marker,
.timeline-track--picking .incoherence-range-marker {
  pointer-events: none;
}

/* Lie range overlay: taller than the track and semi-transparent so idea blocks stay visible. */
.incoherence-range-marker {
  position: absolute;
  top: -3px;
  bottom: -3px;
  height: auto;
  background: rgba(229, 57, 53, 0.4);
  border: 1px solid rgba(229, 57, 53, 0.9);
  border-radius: 2px;
  z-index: 2;
}

.coherence-dip-graph-wrap {
  width: 100%;
  height: 64px;
  margin-top: 0;
  position: relative;
  top: 0;
  overflow: hidden;
  border-radius: 0;
}

.timeline-guide-line {
  position: absolute;
  top: 3px;
  bottom: 0;
  width: 1px;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(255, 255, 255, 0.8) 55%,
    rgba(255, 255, 255, 0.45) 100%
  );
  transform: translateX(-50%);
  pointer-events: none;
  z-index: 2;
}

.coherence-dip-graph {
  display: block;
  width: 100%;
  height: 100%;
}

.timeline-progress {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #ffffff;
  border-radius: 3px;
  transition: width 0.1s linear;
}

.timeline-progress-preview {
  background: rgba(255, 255, 255, 0.35);
  transition: none;
  border-radius: 0 !important;
  z-index: 2;
  pointer-events: none;
}

.timeline-thumb {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  cursor: grab;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease;
  z-index: 3;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.timeline-thumb:hover {
  transform: translate(-50%, -50%) scale(1.2);
}

.timeline-thumb:active,
.timeline-thumb.dragging {
  cursor: grabbing;
  transform: translate(-50%, -50%) scale(1.4);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
  z-index: 3;
}

.gesture-marker {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 20px;
  border-radius: 2px;
  cursor: pointer;
  z-index: 1;
  transition: all 0.2s ease;
}

.gesture-marker:hover {
  transform: translate(-50%, -50%) scale(1.2);
}

.gesture-marker.marker-low {
  background: #d32f2f;
  box-shadow:
    0 0 0 2px rgba(211, 47, 47, 0.6),
    0 2px 4px rgba(0, 0, 0, 0.3);
  height: 24px;
}

.gesture-marker.marker-medium {
  background: #ff9800;
  box-shadow:
    0 0 0 2px rgba(255, 152, 0, 0.6),
    0 2px 4px rgba(0, 0, 0, 0.3);
  height: 20px;
}

/* Coherence timeline markers: same tiers/palette as semaphore (good/warn/bad). */
.gesture-marker.marker-good {
  background: #2e7d32;
  box-shadow:
    0 0 0 2px rgba(46, 125, 50, 0.45),
    0 2px 4px rgba(0, 0, 0, 0.3);
  height: 18px;
}

.gesture-marker.marker-warn {
  background: #f57c00;
  box-shadow:
    0 0 0 2px rgba(245, 124, 0, 0.45),
    0 2px 4px rgba(0, 0, 0, 0.3);
  height: 21px;
}

.gesture-marker.marker-bad {
  background: #c62828;
  box-shadow:
    0 0 0 3px rgba(198, 40, 40, 0.5),
    0 2px 4px rgba(0, 0, 0, 0.3);
  width: 12px;
  height: 24px;
}

.controls-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.time-display {
  color: white;
  font-size: 12px;
  font-weight: 500;
  min-width: 110px;
}

.speed-btn {
  min-width: 50px;
}

.landmarks-controls {
  display: flex;
  justify-content: center;
}

.auto-pause-control {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

</style>

<style lang="scss">
.footer-settings-menu-panel .footer-settings-menu-list {
  max-height: min(720px, 90vh);
  overflow-y: auto;
}
</style>
